"use client";

/**
 * Notion 스타일 FOCUS 기반 테이블 grip 컨트롤러.
 *
 * 동작(Notion 레퍼런스):
 *  1) 셀 focus 시 → 그 셀 테두리에 하이라이트(focus 보더) 표시
 *  2) 셀 focus 시 → 상(열)·좌(행)·우(셀) 보더에 "짧고 두꺼운 gutter 바"만 표시
 *     (발판 grip은 아직 노출하지 않음)
 *  3) gutter에 hover 시 → 그 자리에 발판 grip(드래그 점 / 드롭다운 화살표) 노출
 *  4) grip 클릭 시 → 드롭다운 메뉴
 *
 * BlockNoteView는 tableHandles={false}로 기본 hover 핸들을 끄고, 이 컨트롤러를
 * 자식으로 렌더한다. grip/메뉴/드래그는 BlockNote 공개 컴포넌트(TableHandle,
 * TableCellButton)와 코어 editor.tableHandles 메서드를 재사용한다.
 *
 * 핵심 사실:
 * - tableHandles={false}는 React 컨트롤러만 끄고 코어 플러그인은 유지된다 →
 *   colDragStart/rowDragStart/dragEnd/메뉴 동작 재사용 가능.
 * - 메뉴 동작(add/remove)은 우리가 넘기는 index prop으로 동작하므로 안전하다.
 * - 드래그만 코어 hover 상태에 의존 → gutter pointer-enter 시 합성 mousemove로
 *   코어 인덱스를 포커스 셀과 동기화한다(TS-private view 미접근).
 * - grip을 클릭/드래그하면 ProseMirror 선택이 셀 밖으로 바뀌어 focused가 즉시
 *   해제될 수 있다 → 상호작용(pointerdown/메뉴/드래그) 동안 recompute를 freeze.
 */

import {
  BlockSchema,
  InlineContentSchema,
  StyleSchema,
} from "@blocknote/core";
import {
  ExtendButton,
  TableCellButton,
  TableHandle,
  useBlockNoteEditor,
  useEditorContentOrSelectionChange,
  useExtendButtonsPositioning,
  useUIPluginState,
} from "@blocknote/react";
import { autoUpdate, FloatingPortal } from "@floating-ui/react";
import type { DragEvent as ReactDragEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useFocusedCellHandlePositioning,
  useTableCornerPositioning,
} from "./hooks/useFocusedCellHandlePositioning";
import { LumirTableCellMenu } from "./color/LumirColorControls";
import {
  measureTableForScale,
  setTableScalePreview,
  commitTableScale,
} from "../extensions/tableScaling";
import { findTableNodePos } from "../utils/prosemirror-table-utils";
import {
  TABLE_SCALE_MIN_COL_WIDTH,
  TABLE_SCALE_MAX,
  ROW_RESIZE_MIN_HEIGHT,
} from "../constants/limits";

type FocusedCell = {
  block: any;
  rowIndex: number;
  colIndex: number;
  cellEl: HTMLElement;
  tbodyEl: HTMLElement; // 표(tbody) — gutter 표-가장자리 배치 + 열/행 하이라이트 범위용
  widgetContainer: HTMLElement;
};

/**
 * 포커스된 셀 중앙에 합성 mousemove를 디스패치해 코어 TableHandles 플러그인의
 * hover 상태(rowIndex/colIndex/referencePosCell/tablePos/...)를 포커스 셀과
 * 동기화한다. 코어 리스너(pmView.dom)가 직접 받아 "cell" 분기를 실행한다.
 * 마우스 버튼이 눌리지 않은 상태(pointer-enter)에서만 호출해야 한다.
 */
function syncCoreHoverToFocusedCell(cellEl: HTMLElement) {
  const r = cellEl.getBoundingClientRect();
  cellEl.dispatchEvent(
    new MouseEvent("mousemove", {
      bubbles: true,
      cancelable: true,
      view: window,
      clientX: r.left + r.width / 2,
      clientY: r.top + r.height / 2,
    }),
  );
}

export function LumirTableHandlesController() {
  const editor = useBlockNoteEditor<
    BlockSchema,
    InlineContentSchema,
    StyleSchema
  >();

  const [focused, setFocused] = useState<FocusedCell | null>(null);
  const [menuContainerRef, setMenuContainerRef] =
    useState<HTMLDivElement | null>(null);
  // overlay 엘리먼트는 FloatingPortal로 한 틱 늦게 마운트되므로 state-backed ref로
  // 잡아, 실제 마운트 시점에 사이징 effect가 재실행되도록 한다.
  const [overlayEl, setOverlayEl] = useState<HTMLDivElement | null>(null);

  // 어떤 gutter의 메뉴가 열려 있는지(활성 파란 발판 + 열림 동안 발판 유지용).
  const [openMenu, setOpenMenu] = useState<"col" | "row" | "cell" | null>(null);

  // 상호작용 중에는 selection 변경으로 focused가 해제되지 않도록 recompute를 멈춘다.
  const frozenRef = useRef(false);
  const menuOpenRef = useRef(false);
  const draggingRef = useRef(false);

  // 그립 메뉴의 행/열 삭제는 코어 removeRowOrColumn(= view.tablePos/selection 의존)을
  // 쓰는데, grip 클릭으로 ProseMirror 선택이 표 밖으로 빠지면 그 경로가 실패한다(첫 열/행
  // 삭제 안 됨). 포커스된 표 id를 에디터에 노출해, LumirEditor의 삭제 래퍼가 선택/hover와
  // 무관하게 정확한 표를 찾도록 한다. focused는 상호작용 중 freeze되어 유지된다.
  useEffect(() => {
    (editor as any).__lumirActiveTableId = focused?.block?.id ?? null;
  }, [editor, focused]);

  const recompute = useCallback(() => {
    if (frozenRef.current) {
      return;
    }

    const th = editor.tableHandles;
    const view = editor.prosemirrorView;
    if (!th || !view) {
      setFocused(null);
      return;
    }

    let cellEl: HTMLElement | null = null;
    try {
      const { node } = view.domAtPos(view.state.selection.from);
      const el =
        node.nodeType === Node.TEXT_NODE
          ? (node.parentElement as HTMLElement | null)
          : (node as HTMLElement);
      cellEl = (el?.closest?.("td, th") as HTMLElement | null) ?? null;
    } catch {
      cellEl = null;
    }
    if (!cellEl || !cellEl.isConnected) {
      setFocused(null);
      return;
    }

    const blockId = cellEl.closest("[data-id]")?.getAttribute("data-id");
    const block = blockId ? editor.getBlock(blockId) : undefined;
    if (!block || block.type !== "table") {
      setFocused(null);
      return;
    }

    const cellSel = th.getCellSelection();
    if (!cellSel) {
      setFocused(null);
      return;
    }

    const widgetContainer = cellEl
      .closest(".tableWrapper")
      ?.querySelector(".table-widgets-container") as HTMLElement | null;
    const tbodyEl = cellEl.closest("tbody") as HTMLElement | null;
    if (!widgetContainer || !tbodyEl) {
      setFocused(null);
      return;
    }

    setFocused({
      block,
      rowIndex: cellSel.from.row,
      colIndex: cellSel.from.col,
      cellEl,
      tbodyEl,
      widgetContainer,
    });
  }, [editor]);

  useEditorContentOrSelectionChange(recompute, editor);
  useEffect(() => {
    recompute();
  }, [recompute]);

  // pointerdown으로 freeze한 상호작용이 메뉴/드래그로 이어지지 않은 경우의 안전장치.
  useEffect(() => {
    const onUp = () => {
      requestAnimationFrame(() => {
        if (!menuOpenRef.current && !draggingRef.current && frozenRef.current) {
          frozenRef.current = false;
          recompute();
        }
      });
    };
    window.addEventListener("pointerup", onUp);
    return () => window.removeEventListener("pointerup", onUp);
  }, [recompute]);

  // 활성 보더 오버레이 위치/크기 갱신(스크롤/리사이즈 자동 추적).
  // openMenu에 따라 범위가 달라진다: 열 메뉴→열 전체, 행 메뉴→행 전체, 그 외→셀.
  useEffect(() => {
    const f = focused;
    if (!f || !overlayEl) {
      return;
    }
    // 보더 두께(2px)의 절반 = PAD(1px). 정수/DPR 반올림으로 거터와 정렬 유지.
    const PAD = 1;
    const update = () => {
      const cr = f.cellEl.getBoundingClientRect();
      const tr = f.tbodyEl.getBoundingClientRect();
      const kr = f.widgetContainer.getBoundingClientRect();
      // 활성 보더 범위: 열 메뉴=열 전체(표 상하), 행 메뉴=행 전체(표 좌우), 그 외=셀
      const x1 = openMenu === "row" ? tr.left : cr.left;
      const y1 = openMenu === "col" ? tr.top : cr.top;
      const x2 = openMenu === "row" ? tr.right : cr.right;
      const y2 = openMenu === "col" ? tr.bottom : cr.bottom;
      const dpr = window.devicePixelRatio || 1;
      const rd = (v: number) => Math.round(v * dpr) / dpr;
      const left = rd(x1 - kr.left) - PAD;
      const top = rd(y1 - kr.top) - PAD;
      const right = rd(x2 - kr.left) + PAD;
      const bottom = rd(y2 - kr.top) + PAD;
      overlayEl.style.transform = `translate(${left}px, ${top}px)`;
      overlayEl.style.width = `${right - left}px`;
      overlayEl.style.height = `${bottom - top}px`;
    };
    update();
    const stopAutoUpdate = autoUpdate(f.cellEl, overlayEl, update);
    // 다른 행 높이 변경(행 리사이즈 등)으로 표가 reflow되면 focus 셀 자체는 크기가
    // 안 변해 autoUpdate가 발화하지 않는다. 특히 "아래" 행이 커지면 widgetContainer가
    // 내려가 오버레이가 셀 밖으로 드리프트한다 → tbody 크기를 직접 관찰해 재포지셔닝.
    const ro = new ResizeObserver(update);
    ro.observe(f.tbodyEl);
    return () => {
      stopAutoUpdate();
      ro.disconnect();
    };
  }, [focused, overlayEl, openMenu]);

  const cellEl = focused?.cellEl ?? null;
  const tbodyEl = focused?.tbodyEl ?? null;
  const show = focused !== null;
  const rowHandle = useFocusedCellHandlePositioning(cellEl, tbodyEl, "row", show);
  const colHandle = useFocusedCellHandlePositioning(cellEl, tbodyEl, "col", show);
  const cellHandle = useFocusedCellHandlePositioning(
    cellEl,
    tbodyEl,
    "cell",
    show,
  );
  const th = editor.tableHandles;

  // 표 우측/하단 hover 시 "행/열 추가" ExtendButton — 코어 hover 상태 기반(focus와 독립).
  const coreState = useUIPluginState(
    editor.tableHandles!.onUpdate.bind(editor.tableHandles!),
  );
  const { addOrRemoveColumnsButton, addOrRemoveRowsButton } =
    useExtendButtonsPositioning(
      coreState?.showAddOrRemoveColumnsButton || false,
      coreState?.showAddOrRemoveRowsButton || false,
      coreState?.referencePosTable || null,
    );
  const onStartExtend = useCallback(() => {
    editor.tableHandles?.freezeHandles();
  }, [editor]);
  const onEndExtend = useCallback(() => {
    editor.tableHandles?.unfreezeHandles();
  }, [editor]);

  // 표 우하단 코너 스케일 hit-zone(focus 독립, 코어 hover 기반 → 셀 클릭 없이도 노출).
  const tableCorner = useTableCornerPositioning(
    coreState?.referencePosTable ?? null,
    !!coreState?.widgetContainer,
  );

  // 메뉴 열림/닫힘 핸들러(컨트롤별). 열림 동안 frozen 유지 + openMenu로 활성 표시.
  const menuHandlers = useMemo(() => {
    const mk = (kind: "col" | "row" | "cell") => ({
      freeze: () => {
        menuOpenRef.current = true;
        frozenRef.current = true;
        setOpenMenu(kind);
        editor.tableHandles?.freezeHandles();
      },
      unfreeze: () => {
        menuOpenRef.current = false;
        frozenRef.current = false;
        setOpenMenu(null);
        editor.tableHandles?.unfreezeHandles();
        recompute();
      },
    });
    return { col: mk("col"), row: mk("row"), cell: mk("cell") };
  }, [editor, recompute]);

  const onGutterPointerDown = useCallback(() => {
    frozenRef.current = true;
  }, []);

  const onGutterPointerEnter = useCallback(
    (e: { buttons: number }) => {
      if (e.buttons === 0 && focused) {
        syncCoreHoverToFocusedCell(focused.cellEl);
      }
    },
    [focused],
  );

  const makeDragStart = useCallback(
    (dir: "row" | "col") => (e: ReactDragEvent) => {
      draggingRef.current = true;
      frozenRef.current = true;
      if (dir === "row") {
        editor.tableHandles?.rowDragStart(e as unknown as DragEvent);
      } else {
        editor.tableHandles?.colDragStart(e as unknown as DragEvent);
      }
    },
    [editor],
  );

  const onDragEnd = useCallback(() => {
    editor.tableHandles?.dragEnd();
    draggingRef.current = false;
    frozenRef.current = false;
    recompute();
  }, [editor, recompute]);

  const noop = useCallback(() => {}, []);

  // 우하단 코너 드래그 → 표 전체 종횡비 고정 스케일.
  // hit-zone은 에디터 DOM 밖(오버레이)이라 row/column 리사이즈와 충돌하지 않는다.
  // 대상 표는 코어 hover 상태(coreState.block) → 셀 focus 없이도 동작.
  // 드래그 동안 freezeHandles로 코어 hover 상태를 고정(마우스가 표를 벗어나도 유지).
  const onScaleStart = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const view = (editor as any).prosemirrorView;
      const blockId = coreState?.block?.id;
      if (!view || !blockId) return;
      const tablePos = findTableNodePos((editor as any)._tiptapEditor, blockId);
      if (tablePos < 0) return;
      const base = measureTableForScale(view, tablePos);
      if (!base) return;

      const minScale = Math.max(
        TABLE_SCALE_MIN_COL_WIDTH / Math.max(1, Math.min(...base.colWidths)),
        ROW_RESIZE_MIN_HEIGHT / Math.max(1, Math.min(...base.rowHeights)),
      );
      const startX = e.clientX;
      const startY = e.clientY;
      let current = base;
      editor.tableHandles?.freezeHandles();

      const onMove = (me: PointerEvent) => {
        if (me.buttons === 0) return onUp();
        const newW = base.origW + (me.clientX - startX);
        const newH = base.origH + (me.clientY - startY);
        const scale = Math.min(
          TABLE_SCALE_MAX,
          Math.max(minScale, Math.max(newW / base.origW, newH / base.origH)),
        );
        current = { ...base, scale };
        setTableScalePreview(view, current);
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        // ⚠️ rowResizing 교훈: 프리뷰 제거 후 커밋(스타일 충돌 방지).
        setTableScalePreview(view, null);
        commitTableScale(view, current);
        editor.tableHandles?.unfreezeHandles();
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [editor, coreState],
  );

  return (
    <>
      {/* 메뉴 컨테이너: grip 메뉴가 createPortal로 그려지는 대상. portal 밖에 둔다. */}
      <div ref={setMenuContainerRef} />
      {th && focused && menuContainerRef && (
        <FloatingPortal root={focused.widgetContainer}>
          {/* focus 보더 오버레이 (셀을 덮는 비클릭 테두리) */}
          <div ref={setOverlayEl} className="lumir-tbl-cell-focus" />

          {/* 상(top) gutter → 열(column) */}
          {colHandle.isMounted && (
            <div
              ref={colHandle.ref}
              style={colHandle.style}
              className={
                "lumir-tbl-gutter-wrap lumir-tbl-gutter-wrap--col" +
                (openMenu === "col" ? " lumir-tbl-gutter-wrap--active" : "")
              }
              onPointerEnter={onGutterPointerEnter}
              onPointerDown={onGutterPointerDown}
            >
              <span className="lumir-tbl-gutter" />
              <div className="lumir-tbl-grip">
                <TableHandle
                  editor={editor as any}
                  orientation="column"
                  index={focused.colIndex}
                  block={focused.block}
                  dragStart={makeDragStart("col")}
                  dragEnd={onDragEnd}
                  freezeHandles={menuHandlers.col.freeze}
                  unfreezeHandles={menuHandlers.col.unfreeze}
                  menuContainer={menuContainerRef}
                  showOtherSide={noop}
                  hideOtherSide={noop}
                />
              </div>
            </div>
          )}

          {/* 좌(left) gutter → 행(row) */}
          {rowHandle.isMounted && (
            <div
              ref={rowHandle.ref}
              style={rowHandle.style}
              className={
                "lumir-tbl-gutter-wrap lumir-tbl-gutter-wrap--row" +
                (openMenu === "row" ? " lumir-tbl-gutter-wrap--active" : "")
              }
              onPointerEnter={onGutterPointerEnter}
              onPointerDown={onGutterPointerDown}
            >
              <span className="lumir-tbl-gutter" />
              <div className="lumir-tbl-grip">
                <TableHandle
                  editor={editor as any}
                  orientation="row"
                  index={focused.rowIndex}
                  block={focused.block}
                  dragStart={makeDragStart("row")}
                  dragEnd={onDragEnd}
                  freezeHandles={menuHandlers.row.freeze}
                  unfreezeHandles={menuHandlers.row.unfreeze}
                  menuContainer={menuContainerRef}
                  showOtherSide={noop}
                  hideOtherSide={noop}
                />
              </div>
            </div>
          )}

          {/* 우(right) → 파란 선택 앵커(rest) / hover 시 셀(색상·분할) 메뉴.
              행/열 grip 메뉴가 열려 범위 하이라이트 중에는 노출하지 않는다. */}
          {cellHandle.isMounted && openMenu !== "col" && openMenu !== "row" && (
            <div
              ref={cellHandle.ref}
              style={cellHandle.style}
              className={
                "lumir-tbl-gutter-wrap lumir-tbl-gutter-wrap--cell" +
                (openMenu === "cell" ? " lumir-tbl-gutter-wrap--active" : "")
              }
              onPointerDown={onGutterPointerDown}
            >
              <span className="lumir-tbl-gutter" />
              <div className="lumir-tbl-grip">
                <TableCellButton
                  editor={editor as any}
                  rowIndex={focused.rowIndex}
                  colIndex={focused.colIndex}
                  block={focused.block}
                  tableCellMenu={LumirTableCellMenu as any}
                  menuContainer={menuContainerRef}
                  freezeHandles={menuHandlers.cell.freeze}
                  unfreezeHandles={menuHandlers.cell.unfreeze}
                />
              </div>
            </div>
          )}
        </FloatingPortal>
      )}

      {/* 표 우측/하단 hover 시 행/열 추가(ExtendButton). focus와 무관하게 코어 hover 상태로 표시. */}
      {th && coreState?.widgetContainer && (
        <FloatingPortal root={coreState.widgetContainer}>
          <div ref={addOrRemoveRowsButton.ref} style={addOrRemoveRowsButton.style}>
            <ExtendButton
              editor={editor as any}
              orientation="addOrRemoveRows"
              block={coreState.block}
              onMouseDown={onStartExtend}
              onMouseUp={onEndExtend}
            />
          </div>
          <div
            ref={addOrRemoveColumnsButton.ref}
            style={addOrRemoveColumnsButton.style}
          >
            <ExtendButton
              editor={editor as any}
              orientation="addOrRemoveColumns"
              block={coreState.block}
              onMouseDown={onStartExtend}
              onMouseUp={onEndExtend}
            />
          </div>

          {/* 우하단 코너 → 표 전체 종횡비 고정 스케일(투명 hit-zone, focus 독립).
              hover된 표 기준이라 셀 클릭 없이도 동작. */}
          {tableCorner.isMounted && (
            <div
              ref={tableCorner.ref}
              style={tableCorner.style}
              className="lumir-tbl-scale-handle"
              title="표 크기 조절 (종횡비 고정)"
              onPointerDown={onScaleStart}
            />
          )}
        </FloatingPortal>
      )}
    </>
  );
}
